
/**
 * Module dependencies.
 */

import { color } from 'src/styles/utils';
import { myRevigresRoutes } from 'src/core/routes';
import { regexes } from 'src/core/utils/regexes';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNewsletter } from 'src/api/app/forms/newsletter/use-newsletter';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';

/**
 * `Props` type.
 */

type Props = {
  email: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  background-color: ${color('grey100')};
  height: 100vh;
  position: relative;
`;

/**
 * `UnsubscribeNewsletter` page.
 */

const UnsubscribeNewsletter = (props: Props): ReactElement => {
  const router = useRouter();
  const { locale, translate } = useTranslate();
  const email = useMemo(() => {
    return props?.email ?? get(router, 'query.email');
  }, [props.email, router]);

  const { isSubmitting, onUnsubscribeNewsletter } = useNewsletter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleUnsubscribeNewsletter = useCallback(async () => {
    const recaptchaToken = await executeRecaptcha('unsubscribeNewsletter').then(token => token);

    if (recaptchaToken) {
      onUnsubscribeNewsletter({
        email,
        recaptchaToken
      }).then(() => {
        router.push(myRevigresRoutes.home, undefined, { locale });
      });
    }
  }, [email, executeRecaptcha, locale, onUnsubscribeNewsletter, router]);

  useNavbarTheme({ theme: 'dark' });

  useEffect(() => {
    handleUnsubscribeNewsletter();
  }, [email, handleUnsubscribeNewsletter, router]);

  return (
    <Section>
      <Metatags pageTitle={translate('footer:newsletter.unsubscribe.title')} />

      <Loading active={isSubmitting} />
    </Section>
  );
};

/**
 * Export `getServerSideProps`.
 */

// eslint-disable-next-line require-await
export async function getServerSideProps({ query }) {
  const email = query?.email;

  if (email && !regexes.email.test(email)) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      email
    }
  };
}

/**
 * Export `UnsubscribeNewsletter` page.
 */

export default UnsubscribeNewsletter;
